import { Link } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination, EffectFade } from "swiper/modules";
import FadeInSection from "../animations/FadeInSection";
import { ShoppingBag } from "lucide-react";
import { miniFeatures } from "@/constants/home";
import { t } from "i18next";
import MiniFeature from "./hero/MiniFeatures";
import "swiper/swiper-bundle.css";
import type { HeroSlideType } from "@/types/home-type";
import ImageSlider from "./hero/ImageSlider";
import { useTranslation } from "react-i18next";

export default function HeroSection({ slides }: { slides: HeroSlideType[] }) {
    const { i18n } = useTranslation();
    const lang = i18n.language as "id" | "en" | "my";
    const baseRoute = `/${lang || "id"}`;

    return (
        <FadeInSection>
            <div className="relative">
                <Swiper
                    modules={[Autoplay, Pagination, EffectFade]}
                    effect="fade"
                    // without crossFade, previous slides stay fully opaque underneath
                    // and show below a shorter active slide
                    fadeEffect={{ crossFade: true }}
                    autoplay={{
                        delay: 5000,
                        disableOnInteraction: false,
                    }}
                    pagination={{
                        clickable: true,
                        bulletClass:
                            "swiper-pagination-bullet !bg-gold !opacity-30",
                        bulletActiveClass:
                            "swiper-pagination-bullet-active !opacity-100 !w-6 !rounded-full transition-all duration-300",
                        el: ".hero-pagination",
                    }}
                    loop={slides.length > 1}
                    // overflow-visible: swiper's default overflow:hidden cuts the card
                    // shadow flat at the bottom corners
                    className="w-full -mt-4 md:mt-4 overflow-visible!">
                    {slides.map((slide, idx) => (
                        // h-auto + h-full: every slide stretches to the tallest one
                        <SwiperSlide key={idx} className="h-auto!">
                            <div className="w-full h-full isolate bg-surface dark:bg-dark-2 rounded-3xl md:rounded-4xl overflow-hidden flex flex-col md:flex-row items-stretch min-h-62.5 md:min-h-90 relative border border-gold/5 dark:border-gold/10">
                                {/* Text Content */}
                                <div className="p-6 md:px-10 md:py-8 lg:px-20 w-full md:w-[60%] z-20 relative text-left">
                                    <h1 className="text-xl md:text-4xl lg:text-5xl font-serif font-bold text-dark dark:text-surface mb-2 md:mb-4 leading-snug md:leading-[1.15] max-w-[65%] md:max-w-none">
                                        {slide.title[lang]
                                            .split(" ")
                                            .map((word, i) => (
                                                <span
                                                    key={i}
                                                    className={
                                                        i >=
                                                        slide.title[lang].split(
                                                            " ",
                                                        ).length -
                                                            3
                                                            ? "text-gold dark:text-gold-light"
                                                            : ""
                                                    }>
                                                    {word}{" "}
                                                </span>
                                            ))}
                                    </h1>
                                    <p className="text-dark/70 dark:text-white/70 text-[11px] sm:text-xs md:text-base mb-4 max-w-[60%] md:max-w-sm leading-relaxed">
                                        {slide.desc[lang]}
                                    </p>

                                    <Link
                                        to={`${baseRoute}/shop`}
                                        className="inline-flex items-center gap-1.5 md:gap-2 bg-gold hover:opacity-90 text-white font-semibold py-2 px-4 md:py-3 md:px-6 rounded-full transition-all duration-300 shadow-sm mb-2 md:mb-6 text-[10px] sm:text-xs md:text-base w-max">
                                        <ShoppingBag className="w-3.5 h-3.5 md:w-4.5 md:h-4.5" />
                                        {t("home.hero.cta_shop")}
                                    </Link>

                                    {/* Mini Features: hidden saja di mobile */}
                                    <div className="hidden md:flex flex-wrap items-start gap-4 sm:gap-6 lg:gap-8 mt-auto md:mt-0">
                                        {miniFeatures(t).map((feat, i) => (
                                            <MiniFeature
                                                key={`mini-feature-${i}`}
                                                icon={feat.icon}
                                                label={feat.label}
                                                desc={feat.desc}
                                                withLine={feat.withLine}
                                            />
                                        ))}
                                    </div>
                                </div>

                                {/* img content */}
                                <ImageSlider
                                    img={slide.img}
                                    alt={slide.alt}
                                    isPriority={idx === 0}
                                />
                            </div>
                        </SwiperSlide>
                    ))}
                </Swiper>
                <div className="hero-pagination flex justify-center mt-4 md:mt-6"></div>
            </div>
        </FadeInSection>
    );
}
